import React from "react";
import {
  ScanLine,
  Braces,
  ShieldCheck,
  Workflow,
  CheckCircle2,
  FileText,
  ArrowRightLeft,
  Code2,
  Mailbox,
  PenLine,
  FileCode,
  Eye,
  Brain,
  Sparkles,
  X,
  Users,
  ListChecks,
  CalendarCheck,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/CTA";
import { Seo } from "@/components/Seo";
import { FineTuningCallout } from "@/components/FineTuningCallout";

const features = [
  { icon: ScanLine, title: "Enrollment form OCR software", desc: "Layout-aware OCR reads scanned, faxed, and photographed insurance enrollment forms at any quality — including handwriting, checkboxes, and signatures." },
  { icon: Braces, title: "Insurance enrollment data extractor", desc: "Extracts every field of the member enrollment form: subscriber demographics, dependents, coverage elections, group and policy numbers, and effective dates." },
  { icon: CheckCircle2, title: "Validation & eligibility edits", desc: "Applies SSN, date, plan code, and dependent relationship checks with per-field confidence scoring before data reaches your benefits system." },
  { icon: Workflow, title: "Clean output & APIs", desc: "Delivers structured JSON or 834-ready data to your benefits administration platform, TPA, or carrier eligibility system." },
];

const sections = [
  "Subscriber demographics: name, SSN, DOB, address",
  "Dependent & beneficiary details with relationships",
  "Coverage elections: medical, dental, vision, life plans & tiers",
  "Effective dates, qualifying life events & signatures",
];

const crosswalk = [
  "Subscriber demographics → Loop 2000 (INS, NM1*IL)",
  "Dependents → member-level Loop 2000 (INS*N, NM1*03)",
  "Coverage elections → HD segments (Loop 2300): medical, dental, vision",
  "Effective & termination dates → DTP 356 / 357 segments",
  "Group & policy numbers → REF*0F / REF*1L segments",
];

const capabilities = [
  { icon: Mailbox, title: "Enrollment digital mailroom", desc: "Auto-classify and process every enrollment form hitting your fax lines and mailroom — built to absorb open enrollment season volume spikes." },
  { icon: PenLine, title: "Handwritten enrollment extraction", desc: "AI handwriting recognition captures hand-filled names, SSNs, and dates that defeat traditional OCR." },
  { icon: ListChecks, title: "Checkbox & election capture", desc: "Reads plan options, coverage tiers, waiver checkboxes, and tick-marks — including partially marked selections." },
  { icon: CalendarCheck, title: "Paper enrollment to 834", desc: "Convert scanned and faxed enrollment forms into compliant ANSI X12 834 Benefit Enrollment and Maintenance files automatically." },
];

const devFeatures = [
  { icon: Code2, title: "Enrollment form parsing API", desc: "A healthcare enrollment OCR API that parses enrollment forms to ANSI 834 — POST an image, receive structured JSON/XML in response." },
  { icon: FileCode, title: "SDK: convert image to 834 EDI", desc: "Drop-in SDK to convert an enrollment form image to 834 EDI, backed by an X12 834 generator from paper-form JSON/XML." },
  { icon: ArrowRightLeft, title: "Automated enrollment intake BPO", desc: "Offload high-volume paper enrollment intake — automated enrollment intake BPO with human-in-the-loop QA and SLAs." },
];

const faqs = [
  {
    q: "What is enrollment form OCR?",
    a: "Enrollment form OCR uses AI-powered optical character recognition to read insurance and benefits enrollment forms and convert them into structured, validated data. Hewto.ai reads scans, faxes, and PDFs — including handwriting and checkboxes — and maps every field to clean data with confidence scoring.",
  },
  {
    q: "How does Hewto.ai's insurance enrollment data extractor work?",
    a: "Hewto.ai captures every field of the enrollment form — subscriber demographics, dependent and beneficiary details, coverage elections across medical, dental, vision and life plans, group and policy numbers, and effective dates — then validates the data and delivers 834-ready output via API, webhook, or SFTP.",
  },
  {
    q: "Can Hewto.ai convert paper enrollment forms to EDI 834?",
    a: "Yes. Hewto.ai is a paper enrollment to 834 converter: it OCRs the enrollment form, validates the data, and generates a compliant ANSI X12 834 Benefit Enrollment and Maintenance file. A scanned or faxed enrollment form becomes clean, submittable EDI with no manual keying.",
  },
  {
    q: "How does the enrollment form to X12 834 loop-segment mapping work?",
    a: "Hewto.ai applies an enrollment form to EDI 834 crosswalk that maps each form section to its X12 loop and segment. Subscriber demographics map to Loop 2000 (INS, NM1*IL), dependents to member-level Loop 2000 (INS*N, NM1*03), coverage elections to HD segments in Loop 2300, and effective dates to DTP 356/357 segments.",
  },
  {
    q: "Which enrollment forms does Hewto.ai support?",
    a: "Hewto.ai supports carrier-specific and custom insurance enrollment forms, benefits enrollment and change forms, waiver forms, and qualifying life event documentation. Because the models are vision-based rather than template-based, layout variations across carriers and employers are handled automatically — and Hewto.ai can be fine-tuned on your exact form.",
  },
  {
    q: "Can Hewto.ai handle open enrollment season volume spikes?",
    a: "Yes. The platform scales horizontally to absorb open enrollment bursts — processing high-volume batches from fax servers, digital mailrooms, SFTP drops, and API uploads with SLAs for enterprise throughput, so seasonal peaks never become keying backlogs.",
  },
  {
    q: "Can you read handwritten enrollment forms?",
    a: "Yes. Hewto.ai uses AI handwriting recognition trained on healthcare enrollment documents to capture hand-filled names, SSNs, dates, and signatures accurately — the fields where traditional OCR fails most often.",
  },
  {
    q: "How are coverage elections and checkboxes captured?",
    a: "Hewto.ai's vision-language models read checkboxes, tick-marks, and plan elections visually — including partially marked or ambiguous selections — and assign each election to the correct plan, tier, and coverage level with a per-field confidence score.",
  },
  {
    q: "What accuracy can we expect on enrollment data extraction?",
    a: "Hewto.ai achieves 99%+ field-level extraction accuracy on enrollment forms, with per-field confidence scores and optional human-in-the-loop review so low-confidence fields are flagged before they reach your eligibility or benefits administration system.",
  },
  {
    q: "How are dependents and beneficiaries handled?",
    a: "Dependent and beneficiary blocks are extracted with their relationships, dates of birth, and coverage selections, then mapped to member-level Loop 2000 segments (INS*N, NM1*03) in the 834 output — preserving the full family structure exactly as enrolled.",
  },
  {
    q: "Do you offer an enrollment form parsing API or SDK?",
    a: "Yes. Developers can use our enrollment form parsing API or SDK to convert an enrollment form image to 834 EDI. Send a scanned or faxed form and receive structured JSON/XML plus an X12 834 file generated from it, with webhooks, sandbox credentials, and predictable schemas.",
  },
  {
    q: "Who uses Hewto.ai for enrollment form automation?",
    a: "Carriers, TPAs, brokers, benefits administration platforms, and large self-insured employers use Hewto.ai to automate paper and fax enrollment intake — cutting keying time, reducing eligibility errors, and getting members enrolled correctly the first time.",
  },
  {
    q: "How does Hewto.ai compare to generic OCR or LLMs for enrollment forms?",
    a: "Generic OCR and off-the-shelf LLMs miss checkboxes, misread handwriting, and cannot produce valid X12 834 files. Hewto.ai is purpose-built for healthcare enrollment: vision-language models fine-tuned on your enrollment forms at the click of a button, human-like understanding of checkbox crosses and ticks, the ability to separate watermark text from value text, label text from value text, and background from foreground, plus per-field agentic validation and native 834 output — capabilities you would otherwise have to build yourself.",
  },
  {
    q: "Is Hewto.ai HIPAA compliant for enrollment data?",
    a: "Yes. Enrollment forms contain PII and are processed in HIPAA-aligned environments with encryption in transit and at rest. BAAs are available for covered entities and business associates, with hosted API, private cloud, and on-premise deployment options.",
  },
  {
    q: "How can we evaluate Hewto.ai on our own enrollment forms?",
    a: "Send us a sample batch of your enrollment forms — including your messiest faxes and handwritten elections — and we will run them through the pipeline and share a field-level accuracy report so you can compare Hewto.ai against your current OCR or manual keying process before signing anything.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const EnrollmentFormOCR = () => (
  <PageTransition>
    <Seo
      title="Enrollment Form OCR & Paper Enrollment to 834 Conversion | Hewto.ai"
      description="Hewto.ai automates insurance enrollment form processing — enrollment form OCR software, paper enrollment to EDI 834 converter, and healthcare enrollment OCR API. Turn scanned, faxed, or handwritten enrollment forms into validated ANSI X12 834 Benefit Enrollment files with subscriber, dependent, and coverage election data — all in one pipeline."
      keywords="enrollment form ocr, insurance enrollment data extraction, insurance enrollment automation, enrollment document processing, paper enrollment to 834, enrollment form to edi 834, edi 834 conversion, x12 834 benefit enrollment, 834 enrollment file generation, healthcare enrollment ocr api, enrollment form parsing api, member enrollment data extractor, benefits enrollment automation, benefits enrollment ocr, coverage election extraction, dependent data extraction, open enrollment automation, enrollment intake automation, enrollment digital mailroom, handwritten enrollment form extraction, checkbox extraction enrollment forms, agentic document extraction enrollment, intelligent document processing enrollment, tpa enrollment automation, carrier enrollment intake, benefits administration data capture, qualifying life event processing, sdk convert image to 834 edi, x12 834 generator from paper form"
      canonical="https://www.hewto.ai/enrollment-form-ocr"
      image="https://www.hewto.ai/logo.png"
      jsonLd={faqJsonLd}
    />
    <PageHero
      eyebrow="Products · Enrollment Form OCR Software"
      title="Convert enrollment forms to EDI 834, automatically"
      subtitle="Hewto.ai is enrollment form OCR software and a healthcare enrollment OCR API purpose-built for insurance enrollment — turning scanned, faxed, and handwritten enrollment forms into validated ANSI X12 834 Benefit Enrollment and Maintenance files. End-to-end enrollment intake, from paper to 834 EDI."
    />

    {/* DEMO VIDEO */}
    <section id="demo" className="scroll-mt-20 bg-slate-50 py-24">
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              See it in action
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Watch Hewto.ai process an enrollment form in real time
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              A walkthrough of ingesting, extracting, and validating an insurance enrollment
              form — from scan to 834-ready structured data.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div
            data-testid="demo-video"
            className="relative mt-12 overflow-hidden rounded-3xl border border-slate-200 bg-slate-950 shadow-2xl shadow-brand/10"
          >
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                className="absolute inset-0 h-full w-full"
                src="https://www.youtube.com/embed/Lno5PcBxfDE?rel=0&modestbranding=1"
                title="Hewto.ai insurance enrollment product demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="demo-video-iframe"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {features.map((f, i) => (
          <Reveal key={f.title} delay={(i % 4) * 0.08}>
            <div className="group h-full rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-brand/10">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                <f.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* WHY WE BEAT TRADITIONAL OCR — Agentic VLM */}
    <section className="relative overflow-hidden bg-slate-950 py-24 text-slate-100">
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 10%, rgba(4, 135, 216, 0.35) 0, transparent 45%), radial-gradient(circle at 85% 80%, rgba(4, 135, 216, 0.25) 0, transparent 50%)",
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200/30 bg-brand-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-200">
              <Sparkles className="h-3.5 w-3.5" /> Agentic Document Extraction
            </span>
            <h2 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Why Hewto.ai outperforms traditional enrollment form OCR
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-300">
              We don&apos;t use character-by-character OCR. Hewto.ai runs an agentic document
              extraction pipeline built on <span className="text-white">vision-language models
              fine-tuned specifically for insurance enrollment forms</span>. The model reads the
              form the way a trained benefits administrator does — looking at the whole document,
              its layout, and every visual cue — so it captures the elections and dependents a
              traditional OCR engine silently misses.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Reveal>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Eye className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Vision-first, not character-first</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                A VLM sees the form as a whole — election checkboxes, plan tables, dependent
                blocks, signatures, and overlapping handwriting — instead of stitching guesses
                letter by letter. It reads checkbox crosses and ticks the way a human mind does,
                and tells watermark text apart from value text, label text from value text, and
                background from foreground. Visual context stays intact.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Brain className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Fine-tuned on enrollment forms</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                Our models are fine-tuned on real insurance enrollment forms across carriers,
                layouts, and handwriting styles. They know a dependent block is a family member —
                not just a grid of characters — so extractions map cleanly to fields, not raw text.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Workflow className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Agentic reasoning, per field</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                Each field runs through a purpose-built agent that verifies context, cross-checks
                related sections, and self-corrects when something looks off — the kind of judgment
                a deterministic OCR pipeline simply can&apos;t make.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 grid grid-cols-1 gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-2 md:grid-cols-2 md:gap-0">
            <div className="p-8">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-red-300/80">
                <X className="h-4 w-4" /> Traditional OCR misses
              </div>
              <ul className="mt-5 space-y-3 text-sm text-slate-300">
                {[
                  "Coverage election checkboxes and waiver tick-marks",
                  "Watermarks and background print bleeding into extracted values",
                  "Label text confused with the value text it describes",
                  "Handwritten SSNs, dates of birth, and signatures",
                  "Dependent blocks with ambiguous relationships",
                  "Plan and tier selections buried in dense tables",
                  "Qualifying life event details written in margins",
                  "Effective dates conflicting across form sections",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-brand-500/15 to-brand-500/5 p-8 md:m-2">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-200">
                <CheckCircle2 className="h-4 w-4" /> Hewto.ai captures
              </div>
              <ul className="mt-5 space-y-3 text-sm text-slate-100">
                {[
                  "Every election checkbox — including partially marked ones",
                  "Crosses, ticks, and checkmarks read the way a human eye reads them",
                  "Watermark text separated from real value text — background noise ignored",
                  "Label text distinguished from the value text it describes",
                  "Handwriting in any field, including SSNs and signatures",
                  "Dependents and beneficiaries with correct relationships",
                  "Plan, tier, and coverage level for every line of coverage",
                  "Margin notes and life event details assigned to the right field",
                  "Date conflicts flagged with per-field confidence scores",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-200" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <FineTuningCallout formName="enrollment forms" />

    <section className="bg-slate-50 py-24">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <div>
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              Full form coverage
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Every section of the enrollment form
            </h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              As a complete insurance enrollment data extractor, Hewto.ai captures every section of
              the enrollment form — so nothing is re-keyed by hand during open enrollment or new
              hire onboarding.
            </p>
            <ul className="mt-6 space-y-3">
              {sections.map((s) => (
                <li key={s} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                  <span className="text-slate-700">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="relative">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-lg">
              <div className="flex items-center gap-3 border-b border-slate-100 pb-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand">
                  <Users className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-slate-900">Group Health Enrollment Form</p>
                  <p className="text-xs text-slate-500">2026 Plan Year · Medical / Dental / Vision</p>
                </div>
              </div>
              <div className="mt-5 space-y-4">
                {[
                  ["Subscriber", "John A. Doe"],
                  ["SSN", "•••-••-4321"],
                  ["Dependent 1", "Jane Doe · Spouse"],
                  ["Dependent 2", "Sam Doe · Child"],
                  ["Medical", "PPO Gold · Family"],
                  ["Dental", "DMO Basic · Family"],
                  ["Vision", "Waived"],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between border-b border-dashed border-slate-100 pb-3 text-sm">
                    <span className="text-slate-500">{k}</span>
                    <span className="font-medium text-slate-900">{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute -bottom-6 right-4 w-52 rounded-2xl border border-slate-100 bg-white p-4 shadow-xl">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-brand-50 text-brand">
                  <FileText className="h-3.5 w-3.5" />
                </span>
                Enrollment · extracted
              </div>
              <div className="mt-3 space-y-2 font-mono text-xs">
                {[
                  ["Plan", "PPO-GOLD"],
                  ["Tier", "Family"],
                  ["Eff. date", "01/01/2026"],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-slate-400">{k}</span>
                    <span className="font-semibold text-slate-900">{v}</span>
                  </div>
                ))}
                <div className="flex items-center gap-1.5 pt-1 text-emerald-600">
                  <CheckCircle2 className="h-3.5 w-3.5" /> 99.6% confidence
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* CONVERT TO 834 + CROSSWALK */}
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div>
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              Paper enrollment to 834 converter
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Convert enrollment forms to X12 834
            </h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              Hewto.ai parses enrollment forms and generates a compliant ANSI X12 834 Benefit
              Enrollment and Maintenance file. Every section is mapped through an enrollment form
              to EDI 834 crosswalk, so a scanned or faxed form becomes clean, submittable EDI with
              no manual keying.
            </p>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                Enrollment form → X12 loop / segment mapping
              </p>
              <ul className="mt-4 space-y-3">
                {crosswalk.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-sm">
                    <ArrowRightLeft className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                    <span className="text-slate-700">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-slate-200 bg-slate-950 p-6 shadow-xl">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
              <FileCode className="h-4 w-4 text-brand-200" /> enrollment.834 (X12 834)
            </div>
            <pre className="mt-4 overflow-x-auto whitespace-pre text-xs leading-relaxed text-slate-200">
{`ST*834*0001*005010X220A1~
BGN*00*ENROLL20260101*20260101~
INS*Y*18*030*XN~
REF*0F*GRP12345~
NM1*IL*1*DOE*JOHN****34*XXX4321~
INS*N*01~
NM1*03*1*DOE*JANE~
HD*030**PPO-GOLD~
DTP*356*D8*20260101~`}
            </pre>
            <p className="mt-4 text-xs text-slate-400">
              Generated automatically from a scanned enrollment form — image to 834 EDI file.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    {/* CAPABILITIES */}
    <section className="bg-slate-50 py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              Digital mailroom & data capture
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Enrollment data extraction software for real-world intake
            </h2>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {capabilities.map((c, i) => (
            <Reveal key={c.title} delay={(i % 4) * 0.08}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand">
                  <c.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* DEVELOPER API & SDK */}
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <Reveal>
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
            For developers
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Enrollment form parsing API & healthcare enrollment OCR API
          </h2>
          <p className="mt-4 leading-relaxed text-slate-600">
            Integrate enrollment capture directly: an X12 834 generator from paper-form JSON/XML,
            and an SDK to convert image to 834 EDI.
          </p>
        </div>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
        {devFeatures.map((d, i) => (
          <Reveal key={d.title} delay={i * 0.08}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand text-white">
                <d.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{d.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{d.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="mx-auto max-w-4xl px-5 py-24 sm:px-8">
      <Reveal>
        <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
          Enrollment form OCR FAQ
        </span>
        <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          Insurance enrollment data extraction, answered
        </h2>
        <p className="mt-4 leading-relaxed text-slate-600">
          Common questions about Hewto.ai&apos;s enrollment form OCR software and insurance
          enrollment data extractor for paper and faxed enrollment forms.
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="mt-8" data-testid="enrollment-faq">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
              <AccordionTrigger
                data-testid={`enrollment-faq-trigger-${i}`}
                className="text-left text-base font-semibold text-slate-900 hover:no-underline"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>

    <CTABand />
  </PageTransition>
);

export default EnrollmentFormOCR;
